// packages/api/src/hooks/useMyContacts.ts
// Source: docs/contracts/ESCR-profile-selfservice.md

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  CreateProfileContactInput,
  ProfileContact,
  UpdateProfileContactInput,
} from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

export function useMyContacts() {
  return useQuery({
    queryKey: queryKeys.profile.contacts(),
    queryFn: async (): Promise<ProfileContact[]> => {
      const response = await apiClient.get<ApiSuccess<ProfileContact[]>>('/profile/contacts');
      return response.data.data;
    },
  });
}

export function useCreateMyContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProfileContactInput): Promise<ProfileContact> => {
      const response = await apiClient.post<ApiSuccess<ProfileContact>>('/profile/contacts', input);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.contacts() });
      // Corrigir contato pode limpar a rejeição do bloco no fluxo de correção.
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
    },
  });
}

export function useUpdateMyContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: UpdateProfileContactInput;
    }): Promise<ProfileContact> => {
      const response = await apiClient.patch<ApiSuccess<ProfileContact>>(
        `/profile/contacts/${id}`,
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.contacts() });
      // Corrigir contato pode limpar a rejeição do bloco no fluxo de correção.
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
    },
  });
}

export function useDeleteMyContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }): Promise<void> => {
      await apiClient.delete(`/profile/contacts/${id}`, { data: { reason } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.contacts() });
      // Corrigir contato pode limpar a rejeição do bloco no fluxo de correção.
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
    },
  });
}
